import { SearchItem } from '@yiluhub/yilu-amp-types';
import { YILU_FEATURE } from 'applicationConstants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tzlookup from 'tz-lookup';

import { yiluTracking } from 'utils';

import { useIsFeatureFlagEnabled } from 'context/EnterprisePartnerConfiguration';
import { usePaymentInputContext } from 'context/PaymentInput';
import { PaymentMethods } from 'context/PaymentInput/context';

import { LoungeInteractiveProductSummaryProps } from 'modules/lounges/components';
import { getLoungeStripeLabel, getProductDetails } from 'modules/lounges/utils/getProductDetails';

import { MAX_LOUNGE_GUESTS } from '../../../utils/constants';
import { getActualLoungePrice } from './index';

const useLoungeProductSummaryProps = (
  currentLounge: SearchItem | undefined,
  paymentFormRef: React.RefObject<HTMLElement>,
  pricingOption: string,
) => {
  const { t } = useTranslation();

  const isCreditCardDisabled = useIsFeatureFlagEnabled(YILU_FEATURE.DISABLE_CREDIT_CARD_PAYMENTS);
  const { currentPaymentMethod } = usePaymentInputContext();
  const isCreditCardPaymentMethod = currentPaymentMethod === PaymentMethods.CREDIT_CARD;

  const loungeProductSummaryProps = useMemo(() => {
    if (!currentLounge) {
      return null;
    }

    const {
      name,
      price,
      currency,
      convertedLocalDate,
      travellersCount,
      location,
      providerId,
      termsUrl,
      airportCoordinates,
      allowCreditCardPayment,
    } = getProductDetails(currentLounge);

    const _props: LoungeInteractiveProductSummaryProps = {
      price,
      currency,
      name,
      entryDate: convertedLocalDate,
      displayTimezone:
        airportCoordinates && tzlookup(airportCoordinates?.latitude, airportCoordinates?.longitude),
      travellersCount,
      isEstimatedPriceActive: false,
      location,
      isEditable: false,
      maxTravellersCount: MAX_LOUNGE_GUESTS,
      airportCoordinates,
      termsDetails: {
        termsUrl,
        productDetails: {
          providerId,
          providerName: getLoungeStripeLabel(),
        },
      },
      onSubmit: () => {
        if (paymentFormRef?.current) {
          const paymentForm = paymentFormRef.current;
          paymentForm.dispatchEvent(
            new Event('submit', {
              cancelable: true,
              bubbles: true,
            }),
          );

          yiluTracking.sendGAEvent({
            event: 'pip_cta_clicked',
            category: 'lounge-pip',
            label: 'lounge_pip_cta_clicked',
          });
        }
      },
      isSubmitButtonDisabled:
        (isCreditCardDisabled || !allowCreditCardPayment) && isCreditCardPaymentMethod,
      submitBtnLabel: t('Pay now'),
    };

    const actualPrice = getActualLoungePrice(currentLounge, pricingOption);

    if (actualPrice?.price) {
      _props.price = actualPrice.price;
    }

    if (actualPrice?.currency) {
      _props.currency = actualPrice.currency;
    }

    return _props;
  }, [
    currentLounge,
    paymentFormRef,
    t,
    pricingOption,
    isCreditCardDisabled,
    isCreditCardPaymentMethod,
  ]);

  return loungeProductSummaryProps;
};

export default useLoungeProductSummaryProps;
